/**
 * Native frontend plugin registration for @backstage-community/plugin-graphiql.
 *
 * `convertLegacyPlugin` has been removed. The plugin is now registered as a native
 * `FrontendPlugin` via `createFrontendPlugin`. The legacy `graphiQLRouteRef` (from
 * `@backstage/core-plugin-api`) is still bridged with `convertLegacyRouteRef` because
 * upstream does not export a native RouteRef.
 *
 * PARTIAL DEFERRED — `convertLegacyRouteRef` call remains.
 * Status: BLOCKED (upstream) — upstream must publish a native RouteRef export to eliminate
 * the remaining `convertLegacyRouteRef` call.
 * Upstream: https://github.com/backstage/community-plugins/tree/main/workspaces/graphiql/plugins/graphiql
 */
import {
  ApiBlueprint,
  PageBlueprint,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  createApiFactory,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import { convertLegacyRouteRef } from '@backstage/core-compat-api';
import {
  GraphiQLPage,
  graphiQLRouteRef,
  graphQlBrowseApiRef,
  GraphQLEndpoints,
} from '@backstage-community/plugin-graphiql';
import { createElement } from 'react';

const nativeGraphiQLRouteRef = convertLegacyRouteRef(graphiQLRouteRef);

const graphiqlPage = PageBlueprint.make({
  name: 'root',
  params: {
    path: '/graphiql',
    noHeader: true,
    routeRef: nativeGraphiQLRouteRef,
    loader: async () => createElement(GraphiQLPage),
  },
});

const papiGraphQlApi = ApiBlueprint.make({
  name: 'browse',
  params: define =>
    define(
      createApiFactory({
        api: graphQlBrowseApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          identityApi: identityApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, identityApi, fetchApi }) =>
          GraphQLEndpoints.from([
            {
              id: 'papi',
              title: 'PAPI',
              fetcher: async (params: any) => {
                const { token } = await identityApi.getCredentials();
                const baseUrl = await discoveryApi.getBaseUrl('papi');
                const res = await fetchApi.fetch(`${baseUrl}/graphql`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  body: JSON.stringify(params),
                });

                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(
                    `PAPI proxy error ${res.status}: ${(err as any).error ?? res.statusText}`,
                  );
                }

                return res.json();
              },
            },
          ]),
      }),
    ),
});

export const graphiqlModule = createFrontendPlugin({
  pluginId: 'graphiql',
  routes: {
    root: nativeGraphiQLRouteRef,
  },
  extensions: [graphiqlPage, papiGraphQlApi],
});
